import { type AnalyticsConfig } from "@btnet/services-btnet/handlers/models/analyticsConfig";

/**
 * Product-analytics bootstrap config (Mixpanel + Sawmill), sourced from the anonymous, cacheable
 * APIX init endpoint (`GET /apix/v2/context/init`) instead of the render-blocking, session-aware
 * JScriptGlobalsHandler.ashx. See ADO 282593; the endpoint itself came from ADO 282448.
 *
 * Qualtrics is deliberately not here — it is gated on the script-injected `window.QSI` global
 * rather than an app default — and neither is the `bt-boolean-disableAllAnalytics` opt-out, which
 * stays a local-storage check in `analytics.ts`.
 *
 * `loadInitContext` publishes the resolved section here, and `analytics.ts` reads it synchronously
 * when it builds its plugins. That indirection exists so `analytics.ts` never has to import
 * `utilities/init/bootstrap`: bootstrap's top-level `await` would then land in a module ~240 files
 * depend on, and a top-level await that far up the graph is what reordered the CSS chunks in
 * ADO 283955 (see the note in `bootstrap.ts`). Analytics does not need to initialize pre-render, so
 * deferring the read to the first tracked event is enough to let the publish land first.
 */

function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function asBoolean(value: unknown): boolean {
    return value === true;
}

/** An integer within [min, max], otherwise the shipped default for that field. */
function clampedInteger(value: unknown, min: number, max: number, fallback: number): number {
    return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max
        ? value
        : fallback;
}

function toConfig(
    mixpanel: Record<string, unknown>,
    sawmill: Record<string, unknown>,
): AnalyticsConfig {
    const projectToken = asString(mixpanel.projectToken);
    const tenantId = asString(sawmill.tenantId);
    const ingestionEndpoint = asString(sawmill.ingestionEndpoint);

    return {
        mixpanel: {
            enabled: asBoolean(mixpanel.enabled) && !!projectToken,
            projectToken,
            apiHost: asString(mixpanel.apiHost),
        },
        sawmill: {
            enabled: asBoolean(sawmill.enabled) && !!tenantId && !!ingestionEndpoint,
            tenantId,
            ingestionEndpoint,
            // A threshold below 1 would flush on every event and a payload cap of 0 would trim the
            // whole queue, so an out-of-range value falls back to the shipped default rather than
            // to a boundary that silently changes send behaviour.
            batchThreshold: clampedInteger(sawmill.batchThreshold, 1, 10_000, 1),
            maxCachedEvents: clampedInteger(sawmill.maxCachedEvents, 1, 100_000, 999),
            maxPayloadSizeBytes: clampedInteger(
                sawmill.maxPayloadSizeBytes,
                1,
                10_000_000,
                500_000,
            ),
        },
    };
}

/**
 * Validates the `analytics` section of a raw init payload.
 *
 * Never throws. A plugin is `enabled` only when its own flag is true and its required identifiers
 * are present (Mixpanel project token; Sawmill tenant id + ingestion endpoint) — the same
 * `enable*`/null-guard behaviour these reads have always had.
 *
 * A missing/malformed section disables analytics rather than throwing — matches
 * `normalizeInitContext`'s `disabledConfig()` behaviour for the rest of the payload. (Previously
 * this fell back to `window.btJScriptGlobals.appDefaultFlags` for a payload predating ADO 282593's
 * `analytics` section; that transition window has long since closed now that 282593 is in prod and
 * every cached init response carries the section.)
 */
export function normalizeAnalyticsConfig(raw: unknown): AnalyticsConfig {
    if (raw === null || typeof raw !== "object") {
        return toConfig({}, {});
    }
    const root = raw as Record<string, unknown>;
    return toConfig(
        (root.mixpanel ?? {}) as Record<string, unknown>,
        (root.sawmill ?? {}) as Record<string, unknown>,
    );
}

let publishedConfig: AnalyticsConfig | undefined;

/** Publishes the resolved section. Called by `loadInitContext` once the init payload lands. */
export function publishAnalyticsConfig(config: AnalyticsConfig): void {
    publishedConfig = config;
}

/**
 * The resolved analytics config. Disabled if `loadInitContext` hasn't published one yet — see
 * {@link normalizeAnalyticsConfig}.
 */
export function getAnalyticsConfig(): AnalyticsConfig {
    return publishedConfig ?? normalizeAnalyticsConfig(undefined);
}

/** Test seam — drops the published config so each case starts from a known state. */
export function resetPublishedAnalyticsConfig(): void {
    publishedConfig = undefined;
}
